import { useRef, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import * as Sentry from '@sentry/react';
import { C } from '../styles/tokens';
import ConfirmDialog from './media/ConfirmDialog';
import { useOrganisation } from '../http-hooks/organisation/useOrganisation';
import { useCreateIntegration } from '../http-hooks/integration/useIntegration';
import { useGlobalStore } from '../zustand-store/useGlobalStore';
import { PROJECT_CREATED } from '../constants/gtag-events';
import { trackGA4Event } from '../utils/trackGA4Event';
import { addMixPanelEvent } from '../store/actions';


export default function CreateProjectModal() {
    const { org_id } = useParams();
    const history = useHistory();
    const dispatch = useDispatch();
    const [name, setName] = useState('');

    const open = useGlobalStore((state) => state.showCreateProjectModal);
    const toggle = useGlobalStore((state) => state.toggleCreateProjectModal);
    const { data: org, isLoading: isLoadingOrg } = useOrganisation({ org_id });
    const { mutateAsync: createIntegration, isPending } = useCreateIntegration({ org_id });
    
    const isSubmittingRef = useRef(false);

    const cancelledRef = useRef(false);

    const isBlockedByPlan = !!org && true !== org.gates?.can_create_project;
    const isValid = name.trim().length >= 3;
    const isBusy = isPending || isLoadingOrg;

    const onClose = () => {
        cancelledRef.current = true;
        toggle(false);
        setName('');
    };

    const onCreate = async () => {
        if (!isValid || isBusy || isSubmittingRef.current) return;
        isSubmittingRef.current = true;
        cancelledRef.current = false;
        try {
            const newIntegration = await createIntegration({ integration_name: name.trim() });
            if (cancelledRef.current) return;
            trackGA4Event(PROJECT_CREATED, { project_type: 'integration' });
            dispatch(addMixPanelEvent({
                orgId: org_id, integrationId: newIntegration.id, body: {
                    eventName: "INTEGRATION_SETUP_COMPLETED",
                    eventData: { integration_name: name.trim() },
                }
            }));
            onClose();
            history.push(`/organisation/${org_id}/integration/${newIntegration.id}/content`);
        } catch (error) {

            Sentry.captureException(error);
        } finally {
            isSubmittingRef.current = false;
        }
    };

    const onUpgrade = () => {
        onClose();
        history.push(`/organisation/${org_id}/upgrade?source=create_project_limit`);
    };

    if (isBlockedByPlan) {
        return (
            <ConfirmDialog
                open={open}
                title="Upgrade to create multiple projects"
                message="Create and manage multiple projects by upgrading your account."
                confirmLabel="Upgrade"
                cancelLabel="Cancel"
                danger={false}
                onConfirm={onUpgrade}
                onCancel={onClose}
            />
        );
    }

    return (
        <ConfirmDialog
            open={open}
            title="Create a new project"
            confirmLabel={isPending ? 'Creating…' : 'Create'}
            danger={false}
            confirmDisabled={!isValid || isBusy}
            onConfirm={onCreate}
            onCancel={onClose}
            message={
                <div>
                    <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: C.n600, marginBottom: 6 }}>
                        Project Name
                    </label>
                    <input
                        type="text"
                        autoFocus
                        value={name}
                        onChange={e => setName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') onCreate(); }}
                        placeholder="Enter project name (min. 3 characters)"
                        style={{
                            width: '100%', padding: '9px 12px', borderRadius: 9,
                            border: `1px solid ${C.n200}`, fontSize: 13.5, fontFamily: 'inherit',
                            color: C.n900,
                        }}
                    />
                    {name.length > 0 && !isValid && (
                        <p style={{ fontSize: 12, color: C.n500, margin: '6px 0 0' }}>At least 3 characters</p>
                    )}
                </div>
            }
        />
    );
}
